import {
  CPButton,
  CPFormHelperText,
  CPTextField,
  CPTitle,
} from '@warden-ui/common-components';
import { CPCard } from '@warden-ui/common-components';
import { useState } from 'react';
import { UserNameInput } from '@warden-ui/common-components';
import { Box, Stack } from '@mui/material';
import { isValidPassword, passwordValidations } from '@warden-ui/common';
import { useTranslation } from 'react-i18next';

interface CardContentUserJoinProps {
  teamName: string;
  teamDomain: string;
  skipSignup?: boolean;
  cupixvista?: boolean;
  onClickResetPassword?: (
    firstname: string,
    lastname: string,
    password: string | undefined,
  ) => void;
  errorMessage?: string;
  loading?: boolean;
}

export const CardContentUserJoin = ({
  teamName,
  teamDomain,
  skipSignup = false,
  cupixvista = false,
  onClickResetPassword = () => {},
  ...props
}: CardContentUserJoinProps) => {
  const [t] = useTranslation();
  const prefix = ['user_join'];
  if (cupixvista) {
    prefix.push('cupixvista');
  } else {
    prefix.push('cupixworks');
  }

  if (!skipSignup) {
    prefix.push('signup');
  }

  const i18nPrefix = prefix.join('.');
  const title = t(`${i18nPrefix}.title`);
  const actionLabel = t(`${i18nPrefix}.action`);
  const hint = t(`${i18nPrefix}.hint`, { teamName });

  const [firstname, setFirstname] = useState('');
  const [lastname, setLastname] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const matchPassword = password === confirmPassword;

  const isValidEnter = () => {
    const isValidName = firstname.length > 0 && lastname.length > 0;
    if (skipSignup) {
      return isValidName;
    }
    return isValidName && matchPassword && isValidPassword(password);
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (isValidEnter()) {
      onClickResetPassword(firstname, lastname, password.length > 0 ? password : undefined);
    }
  };

  return (
    <CPCard>
      <CPTitle
        title={title}
        boldTitle={teamName}
        hint={teamDomain}
      />

      {(hint.length > 0) && <span className="cp-hint">{hint}</span>}

      <form onSubmit={handleSubmit} className="w-100">
        <Box className="card-row w-100">
          <Stack spacing={3}>
            <UserNameInput
              firstname={firstname}
              lastname={lastname}
              onFirstnameChange={(val) => setFirstname(val)}
              onLastnameChange={(val) => setLastname(val)}
            />

            {skipSignup ? null : (
              <>
                <Box>
                  <CPTextField
                    id="password"
                    label={t('labels.password')}
                    value={password}
                    type="password"
                    autoFocus={false}
                    onChange={(val) => setPassword(val)}
                    sx={{ mb: 1 }}
                  />

                  {passwordValidations.map((validation) => (
                    <CPFormHelperText
                      key={validation.name}
                      id={`${validation.name}-helper`}
                      message={validation.message}
                      error={!validation.isValid(password)}
                    />
                  ))}
                </Box>

                <CPTextField
                  id="confirm-password"
                  label={t('labels.confirm_password')}
                  value={confirmPassword}
                  type="password"
                  autoFocus={false}
                  onChange={(val) => setConfirmPassword(val)}
                  errorMessage={
                    matchPassword
                      ? props.errorMessage
                      : t('errors.password_mismatch')
                  }
                />
              </>
            )}
          </Stack>
        </Box>
        <Box className="w-100" sx={{ marginTop: 3}}>
          <CPButton
            label={actionLabel}
            loading={props.loading}
            disabled={!isValidEnter()}
          />
        </Box>
      </form>
    </CPCard>
  );
};
